import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { Flame, Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8" data-testid="login-logo">
          <span className="w-9 h-9 rounded-md bg-primary flex items-center justify-center"><Flame className="w-5 h-5 text-white" /></span>
          <span className="font-display text-xl font-bold">DealWisp</span>
        </Link>
        <div className="bg-card border border-white/10 rounded-2xl p-8">
          <h1 className="font-display text-2xl font-bold tracking-tight">Welcome back</h1>
          <p className="text-sm text-muted-foreground mt-1">Log in to manage your deals.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="overline text-muted-foreground">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@shop.com" data-testid="login-email" className="mt-2 bg-background border-white/10" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="overline text-muted-foreground">Password</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="login-forgot-password">Forgot password?</Link>
              </div>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password" className="mt-2 bg-background border-white/10" />
            </div>
            {error && <p className="text-sm text-primary" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-primary hover:bg-secondary" data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log in"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-center text-muted-foreground">
            No account? <Link to="/register" className="text-primary hover:underline" data-testid="login-to-register">Start free trial</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
